import React, { useState, useEffect } from "react";
import { Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { interviewService } from "@/services/serviceFactory";
import { toast } from "@/hooks/use-toast";

interface LastSyncIndicatorProps {
  onSyncUpdate?: () => void;
}

const LastSyncIndicator: React.FC<LastSyncIndicatorProps> = ({
  onSyncUpdate,
}) => {
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      const profile = await interviewService.getUserProfile();
      if (profile.last_calendar_sync) {
        setLastSyncTime(new Date(profile.last_calendar_sync));
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatLastSync = (date: Date | null) => {
    if (!date) return "Never";

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ago`;
    } else if (hours > 0) {
      return `${hours}h ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return "Just now";
    }
  };

  const handleOpenDialog = () => {
    setSelectedDate(lastSyncTime || new Date());
    setIsDialogOpen(true);
  };

  const handleUpdateSyncTime = async () => {
    try {
      setIsUpdating(true);
      const isoString = selectedDate.toISOString();

      await interviewService.updateUserSyncTime(isoString);
      setLastSyncTime(selectedDate);
      setIsDialogOpen(false);

      toast({
        title: "Last Sync Time Updated",
        description: `Last sync time has been manually set to ${selectedDate.toLocaleString()}.`,
        variant: "default",
      });

      // Notify parent component
      if (onSyncUpdate) {
        onSyncUpdate();
      }
    } catch (error) {
      console.error("Error updating sync time:", error);
      toast({
        title: "Update Failed",
        description: "Failed to update last sync time. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
        <Clock className="h-4 w-4" />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 h-auto py-1 px-2"
        onClick={handleOpenDialog}
      >
        <Clock className="h-4 w-4" />
        <span>Last sync: {formatLastSync(lastSyncTime)}</span>
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <span>Manually Set Last Sync Time</span>
            </DialogTitle>
            <DialogDescription>
              Only change this if you understand the implications and need to
              recover from a sync issue.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-amber-800 font-medium mb-2">
                ⚠️ Warning: This action is not recommended
              </p>
              <p className="text-amber-700 text-sm">
                Manually changing the last sync time can cause issues with your
                interview data
              </p>
            </div>
          </div>

          <div className="py-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              New Last Sync Time:
            </label>
            <DateTimePicker date={selectedDate} setDate={setSelectedDate} />
          </div>

          <DialogFooter className="space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSyncTime}
              disabled={isUpdating}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isUpdating ? "Updating..." : "Update Sync Time"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LastSyncIndicator;
